/**
 * @file Checkbox 复选框
 * @description 自定义外观复选框（非原生皮肤）；紧凑；支持 indeterminate。
 *
 * @whenToUse 多选协议、表格行选择、筛选条件。
 * @whenNotToUse 二选一互斥（用 Radio）；开/关设置（用 Switch）。
 */

import {
  useEffect,
  useId,
  useRef,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react'
import { Check, Minus } from 'lucide-react'
import { cn } from '@/lib/cn.ts'

export type CheckboxProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'size'
> & {
  /** 文案（可点选） */
  label?: ReactNode
  /** 部分选中（表格全选等） */
  indeterminate?: boolean
}

export function Checkbox({
  label,
  className,
  disabled,
  id,
  indeterminate = false,
  checked,
  ...rest
}: CheckboxProps) {
  const autoId = useId()
  const inputId = id ?? autoId
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate
    }
  }, [indeterminate])

  return (
    <label
      htmlFor={inputId}
      className={cn(
        'inline-flex cursor-pointer items-center gap-2 text-sm text-foreground transition-opacity duration-200',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
    >
      <span className="relative inline-flex size-4 shrink-0">
        <input
          ref={inputRef}
          id={inputId}
          type="checkbox"
          disabled={disabled}
          checked={checked}
          className="peer absolute inset-0 z-10 cursor-pointer opacity-0 disabled:cursor-not-allowed"
          {...rest}
        />
        <span
          className={cn(
            'pointer-events-none flex size-4 items-center justify-center rounded-sm border-2 border-border bg-raised transition-opacity duration-200 peer-checked:border-primary peer-checked:bg-primary peer-focus-visible:opacity-90 peer-checked:[&>svg]:opacity-100',
            indeterminate && 'border-primary bg-primary [&>svg]:opacity-100',
          )}
          aria-hidden
        >
          {indeterminate ? (
            <Minus className="size-3 text-primary-foreground" />
          ) : (
            <Check className="size-3 text-primary-foreground opacity-0 transition-opacity duration-200" />
          )}
        </span>
      </span>
      {label ? <span>{label}</span> : null}
    </label>
  )
}
